import { ApiError } from './client';
import { humanise, reasonOf, valueAt } from './plain-error';

/**
 * Turns a refused request into something a person can act on.
 *
 * `ZodPipe` answers every schema failure with the headline "Validation failed"
 * plus a list of `{ path, message }` — Zod's own wording against the wire
 * name (`codePrefix: Invalid`). Showing the headline alone tells the author
 * nothing; showing the list raw tells them in the wrong vocabulary. This maps
 * each entry to the label the screen shows for that field and the panel it
 * sits in, so the sentence points at the box to fix:
 *
 *   "Code prefix is not in the expected format in the Identity section — use
 *    capital letters, digits and hyphens, e.g. RG-101."
 *
 * Every error is reported, one per line, not just the first. A non-validation
 * refusal (409, 403, …) already carries a sentence written for people and is
 * passed through unchanged.
 */

export interface FieldMeta {
  /** The label the screen shows for the field. */
  label: string;
  /** The panel/block the field sits in, as titled on screen. */
  section?: string;
  /** What a correct value looks like, when the rule is not obvious from the reason. */
  hint?: string;
}

/** Keyed by the field's wire name — the last non-numeric segment of the path. */
export type FieldMap = Record<string, FieldMeta>;

/** One sentence per invalid field. Empty when the error is not a validation failure. */
export function validationMessages(e: unknown, fields: FieldMap = {}, body?: unknown): string[] {
  if (!(e instanceof ApiError) || e.fieldErrors.length === 0) return [];
  return e.fieldErrors.map(({ path, message }) => {
    const segments = path === '(root)' ? [] : path.split('.');
    const key = [...segments].reverse().find((s) => !/^\d+$/.test(s));
    if (!key) return 'The request was incomplete — refresh the page and try again.';
    const meta = fields[key];
    const label = meta?.label ?? humanise(key);
    const index = segments.find((s) => /^\d+$/.test(s));
    const row = index !== undefined && key !== segments[0] ? ` (row ${Number(index) + 1})` : '';
    const reason = reasonOf(message, valueAt(body, segments));
    const where = meta?.section ? ` in the ${meta.section} section` : '';
    const hint = meta?.hint && reason !== 'is required' ? ` — ${meta.hint}` : '';
    return `${label}${row} ${reason}${where}${hint}.`;
  });
}

/**
 * The message to show for any failed request: every invalid field on its own
 * line when it is a validation failure, the server's own sentence otherwise.
 */
export function describeError(e: unknown, fields: FieldMap = {}, body?: unknown, fallback = 'That did not work'): string {
  const lines = validationMessages(e, fields, body);
  if (lines.length === 1) return lines[0]!;
  if (lines.length > 1) return [`Please correct ${lines.length} fields:`, ...lines.map((l) => `• ${l}`)].join('\n');
  return e instanceof ApiError ? e.message : fallback;
}

/**
 * BG-04 — the figures behind a refused checkout. The 409 carries `code`, `available`, `ceiling`,
 * `requested` and `currency` (all decimal strings) beside its message; `ApiError.detail` is that
 * body. Null for any other refusal, or if the body lacks a usable figure, so the caller falls
 * back to the server's sentence.
 *
 * **`ceiling` is the figure that refused the order, and `available` is not.** Since the client's
 * note of 29-Oct-2026 there is a tolerance band on the budget, so an order a little over the
 * available figure is accepted — which made the old message ("you have 15,000 available") false
 * on an account that would in fact have taken 15,750. The screen states the ceiling.
 *
 * `ceiling` is OPTIONAL here on purpose. The API and the web deploy separately, and a web build
 * that lands first must not show an empty amount against an older API's payload: without it the
 * caller falls through to the server's own sentence, which is correct either way.
 */
export function budgetExceeded(
  e: unknown,
): { available: string; ceiling: string | null; currency: string } | null {
  if (!(e instanceof ApiError) || !e.isConflict) return null;
  const d = e.detail as {
    code?: unknown; available?: unknown; ceiling?: unknown; currency?: unknown;
  } | undefined;
  if (d?.code !== 'BUDGET_EXCEEDED') return null;
  if (typeof d.available !== 'string' || typeof d.currency !== 'string') return null;
  return {
    available: d.available,
    ceiling: typeof d.ceiling === 'string' ? d.ceiling : null,
    currency: d.currency,
  };
}

/**
 * CR-57 / D105 — the account's KYC is not approved, so this order was refused. The 409 carries
 * `code: 'KYC_APPROVAL_REQUIRED'`, `status`, `rejectionReason` and `documentCount` beside its message;
 * `ApiError.detail` is that body. Null for any other refusal, so the caller falls back.
 *
 * Shaped exactly like `budgetExceeded` above, and for the same reason: a retailer who has filled a
 * cart and is refused with "that did not work" will ring their account manager. They need to know
 * WHICH of three things is true — nothing has reached Miraki yet, it has and is being reviewed, or it
 * was refused and here is why — because each has a different next step.
 *
 * Every field but `status` is tolerated as absent. The API and the web deploy separately, and a web
 * build that lands first must not show a half-empty message against an older API's payload: with no
 * `documentCount` it degrades to the PENDING-with-documents wording, which is true in the one case
 * that matters (a pack is on file and waiting), and with no `rejectionReason` it says to ask.
 */
export function kycRequired(
  e: unknown,
): { status: 'PENDING' | 'REJECTED'; reason: string | null; documentCount: number | null } | null {
  if (!(e instanceof ApiError) || !e.isConflict) return null;
  const d = e.detail as {
    code?: unknown; status?: unknown; rejectionReason?: unknown; documentCount?: unknown;
  } | undefined;
  if (d?.code !== 'KYC_APPROVAL_REQUIRED') return null;
  if (d.status !== 'PENDING' && d.status !== 'REJECTED') return null;
  return {
    status: d.status,
    reason: typeof d.rejectionReason === 'string' && d.rejectionReason.trim() !== '' ? d.rejectionReason : null,
    documentCount: typeof d.documentCount === 'number' ? d.documentCount : null,
  };
}

/**
 * Which of the three sentences R-05 shows. Pure, so the choice is tested rather than eyeballed.
 *
 *   `rejected`     it was refused: lead with Miraki's reason;
 *   `needed`       nothing has reached Miraki: there is something to DO;
 *   `reviewing`    Miraki has it: there is something to WAIT for.
 *
 * `documentCount` unknown (an older API) is read as `reviewing`: "we are looking at your documents"
 * is the one claim that cannot send a retailer off to do something they have already done.
 */
export function kycRefusalKind(k: NonNullable<ReturnType<typeof kycRequired>>): 'rejected' | 'needed' | 'reviewing' {
  if (k.status === 'REJECTED') return 'rejected';
  return k.documentCount === 0 ? 'needed' : 'reviewing';
}

